import { useState } from "react";
import { Avatar } from "@/components/Avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StatusBadge, fmtDate, JsonBlock, EmptyState } from "@/components/common";
import { useT } from "@/i18n";

const SEVERITIES = ["", "INFO", "WARN", "SECURITY", "CRITICAL"];

export default function AuditTable({ data, loading, filters, setFilters, showTenant }) {
  const { t } = useT();
  const [selected, setSelected] = useState(null);
  const items = data?.items || [];
  const pages = Math.max(1, Math.ceil((data?.total || 0) / (data?.limit || 25)));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <Input data-testid="audit-search" placeholder={`${t("common.search")}…`} className="max-w-xs bg-card" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })} />
        <div className="flex gap-1">
          {SEVERITIES.map((s) => (
            <button key={s} data-testid={`audit-severity-${s || "all"}`} onClick={() => setFilters({ ...filters, severity: s, page: 1 })} className={`text-[10px] font-mono uppercase px-2 py-1 rounded border transition-colors ${filters.severity === s ? "bg-slate-900 text-white" : "bg-card hover:bg-secondary"}`}>{s || t("common.all")}</button>
          ))}
        </div>
        <span className="ml-auto text-xs text-muted-foreground font-mono" data-testid="audit-total">{data?.total ?? 0} events</span>
      </div>
      <div className="w-full overflow-x-auto border rounded-lg bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>{t("audit.severity")}</TableHead><TableHead>Action</TableHead><TableHead>Actor</TableHead>{showTenant && <TableHead>Tenant</TableHead>}<TableHead>Resource</TableHead><TableHead>IP</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id} data-testid={`audit-row-${a.id}`} className="text-xs">
                <TableCell className="font-mono whitespace-nowrap">{fmtDate(a.created_at)}</TableCell>
                <TableCell><StatusBadge status={a.severity} /></TableCell>
                <TableCell className="font-mono">{a.action}{a.impersonation_id && <span className="ml-1 text-amber-600">[support]</span>}</TableCell>
                <TableCell><span className="flex items-center gap-2"><Avatar userId={a.user_id || a.platform_user_id} v={1} name={a.user_email || a.scope} size={22} testId={`audit-avatar-${a.id}`} />{a.user_email || a.scope}</span></TableCell>
                {showTenant && <TableCell className="font-mono text-[10px]">{a.tenant_id || "—"}</TableCell>}
                <TableCell className="font-mono">{a.resource}{a.resource_id ? ` · ${a.resource_id.slice(-6)}` : ""}</TableCell>
                <TableCell className="font-mono">{a.ip || "—"}</TableCell>
                <TableCell><Button size="sm" variant="ghost" data-testid={`audit-inspect-${a.id}`} onClick={() => setSelected(a)}>{t("audit.inspect")}</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && items.length === 0 && <div className="p-6"><EmptyState title="No audit events" /></div>}
      </div>
      <div className="flex items-center justify-end gap-2 text-xs">
        <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>Prev</Button>
        <span className="font-mono">{filters.page} / {pages}</span>
        <Button size="sm" variant="outline" disabled={filters.page >= pages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>Next</Button>
      </div>
      <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
        <SheetContent className="bg-card w-full sm:max-w-xl overflow-y-auto" data-testid="audit-inspector">
          <SheetHeader><SheetTitle className="font-heading font-mono text-base">{selected?.action}</SheetTitle></SheetHeader>
          {selected && (
            <div className="space-y-4 mt-4 text-xs">
              <div className="grid grid-cols-2 gap-2">{[["Time", fmtDate(selected.created_at)], ["Severity", selected.severity], ["Actor", selected.user_email], ["Scope", selected.scope], ["IP", selected.ip], ["Resource", `${selected.resource} ${selected.resource_id || ""}`], ["Tenant", selected.tenant_id], ["Company", selected.company_id]].map(([k, v]) => <div key={k} className="border rounded p-2"><div className="eyebrow">{k}</div><div className="font-mono break-all">{v || "—"}</div></div>)}</div>
              <div><div className="eyebrow mb-1">Before</div><JsonBlock value={selected.before} /></div>
              <div><div className="eyebrow mb-1">After</div><JsonBlock value={selected.after} /></div>
              {selected.meta && <div><div className="eyebrow mb-1">Meta</div><JsonBlock value={selected.meta} /></div>}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
